// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  AdvisoryDebtLoan,
  DebtAmortization,
} from '@shared/data-access/types/brokerage-types';

export type PendingDebtAmortization = {
  installment_date: string | null;
  total: number;
};

const parseDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);

  return new Date(year!, month! - 1, day);
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getPendingDebtAmortizations = (
  amortizations: DebtAmortization[] = [],
): PendingDebtAmortization[] =>
  amortizations
    .filter(item => item.status === 'pending')
    .map(item => ({
      installment_date: item.installment_date ?? null,
      total: item.total ?? 0,
    }));

export const getLatestInstallmentDate = (
  amortizations: DebtAmortization[] = [],
): string | null => {
  const dates = getPendingDebtAmortizations(amortizations)
    .map(item => item.installment_date)
    .filter((date): date is string => date !== null);

  if (dates.length === 0) {
    return null;
  }

  return dates.reduce((latest, date) => (date > latest ? date : latest), dates[0]!);
};

export const getLoanMaturityDate = (
  loan: Pick<AdvisoryDebtLoan, 'expiration_date' | 'amortizations'>,
): string | null =>
  loan.expiration_date ?? getLatestInstallmentDate(loan.amortizations);

export const isLoanExpired = (
  loan: Pick<AdvisoryDebtLoan, 'expiration_date' | 'amortizations'>,
  referenceDate: Date = new Date(),
): boolean => {
  const maturityDate = getLoanMaturityDate(loan);

  if (!maturityDate) {
    return false;
  }

  return startOfDay(parseDate(maturityDate)) < startOfDay(referenceDate);
};

export const resolveInstitutionLabel = (
  institutionId: string,
  institutionNames: Map<string, string>,
): string => institutionNames.get(institutionId) ?? institutionId;
